import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Image, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import mailImg from 'assets/brand/mail@2x.png';
import zodLocales from 'common/zod/locales';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';
import { checkEmail } from 'helpers/auth/service';
import * as Layout from 'layouts/StepperLayout';

import locales from './locales';
import { UpdateUserEmailDocument } from './mutations/updateUserEmail.gql';
import { EmailChangeQueryDocument } from './queries/getUserEmail.gql';

const formSchema = z
  .object({
    confirmationEmail: z
      .string()
      .min(1, zodLocales.errors.required)
      .email(locales.errors.wrongFormat),
    email: z
      .string()
      .min(1, zodLocales.errors.required)
      .email(locales.errors.wrongFormat),
  })
  .refine(({ confirmationEmail, email }) => email === confirmationEmail, {
    message: locales.errors.differentEmails,
    path: ['confirmationEmail'],
  });

type FormSchema = z.infer<typeof formSchema>;

const EditEmail: FC = () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [emailAlreadyExistsError, setEmailAlreadyExistsError] = useState('');

  const { data, loading: loadingQuery } = useQuery(EmailChangeQueryDocument);
  const [updateUserProfile, { loading: loadingMutation }] = useMutation(
    UpdateUserEmailDocument,
  );

  const profile = data?.viewer.profile;

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    watch,
  } = useForm<FormSchema>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async ({ email }: FormSchema) => {
    setEmailAlreadyExistsError('');
    const { available } = await checkEmail(email);
    if (!available) {
      setEmailAlreadyExistsError(locales.errors.emailAlreadyExists);
      return;
    }
    await updateUserProfile({
      variables: {
        input: {
          email,
        },
      },
    });
    showToast({
      message: locales.success,
      type: 'success',
    });
    history.push('/profile');
  };

  const isNewEmailEqualsCurrent = watch('email') === profile?.email;
  const newEmailEqualsCurrentError = isNewEmailEqualsCurrent
    ? locales.errors.newEmailEqualsCurrent
    : '';
  const isSavingDisabled =
    loadingQuery || loadingMutation || !isValid || isNewEmailEqualsCurrent;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Stack paddingBottom="space-40" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text>{locales.subtitle}</Text>
          </Stack>

          {loadingQuery ? (
            <Loader />
          ) : (
            <Stack spacing="space-24">
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Form.Field
                    error={
                      errors.email?.message ||
                      newEmailEqualsCurrentError ||
                      emailAlreadyExistsError
                    }
                    label={locales.newEmail}
                  >
                    <Form.Input
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setEmailAlreadyExistsError('');
                      }}
                      placeholder="ex: johndoe@mail.com"
                      value={field.value}
                    />
                  </Form.Field>
                )}
              />

              <Form.Field
                error={errors.confirmationEmail?.message}
                label={locales.newEmailConfirmation}
              >
                <Form.Input
                  placeholder="ex: johndoe@mail.com"
                  type="email"
                  {...register('confirmationEmail')}
                />
              </Form.Field>
            </Stack>
          )}
        </Layout.BodyScrollableSection>

        <Layout.FooterButtonsContainer>
          <Button
            icon="tick"
            iconPosition="right"
            isDisabled={isSavingDisabled}
            isLoading={loadingMutation}
            onClick={handleSubmit(onSubmit)}
          >
            {locales.save}
          </Button>
        </Layout.FooterButtonsContainer>
      </Layout.Body>

      <Layout.Sidebar>
        <Image role="presentation" src={mailImg} />
      </Layout.Sidebar>
    </Layout.Root>
  );
};

export default EditEmail;
